import { api } from '@core/api';

/**
 * Payload for POST /{lang}/traditional-grab
 * Matches the legacy enduser frontend payload structure.
 */
/**
 * Attribution fields accepted by the backend (GrabController).
 * Stored in ReservationAttribution entity when at least one is present.
 */
interface ReservationAttributionFields {
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

export interface TraditionalGrabPayload extends ReservationAttributionFields {
  restaurant: number;
  date: string;           // "YYYY-MM-DD"
  time: string;           // e.g., "10:00 AM" or "10:00" depending on hourFormat
  partySize: number;
  menu: number;
  area: number;
  type: 'traditional';
  source: string;
  pairings: unknown[];
  veggie: boolean;
  howManyVeggie: number;
  kids: boolean;
  howManyKids: number;
  allergies: string;
  pets: boolean;
  accesibility: boolean;  // Note: legacy API uses this spelling
  fee: number;
  gratuity: number;
  giftCardToken: string | null;
  suggestedFromWaitingListAlternatives: boolean;
  rescheduleId?: number;
  agentReservation?: boolean;
  guestName?: string;
  guestEmail?: string;
  guestPhone?: string;
  requestGuarantee?: boolean;
}

/**
 * Payload for POST /{lang}/deposit-grab-stripe
 * Same fields as traditional but with guarantee-specific additions.
 */
/**
 * Peru billing data (boleta/factura)
 */
export interface BillPersonalBusiness {
  billType: 'boleta' | 'factura';
  billPersonalName?: string;
  billPersonalDocument?: string;
  billPersonalAddress?: string;
  billBusinessName?: string;
  billBusinessDocument?: string;
  billBusinessAddress?: string;
}

export interface DepositGrabPayload extends ReservationAttributionFields {
  restaurant: number;
  date: string;
  time: string;
  partySize: number;
  menu: number;
  area: number;
  type: 'deposit' | 'no_show';
  source: string;
  pairings: unknown[];
  veggie: boolean;
  howManyVeggie: number;
  kids: boolean;
  howManyKids: number;
  allergies: string;
  pets: boolean;
  accesibility: boolean;
  gratuity: number;
  giftCardToken: string | null;
  suggestedFromWaitingListAlternatives: boolean;
  rescheduleId?: number;
  cardToken: string;
  paymentMethod: string;
  salesTax: number;
  serviceIncluded: boolean;
  tipChargePercent: string;
  payWithCard: boolean;
  agentReservation: boolean;
  partySizeType: string;
  AMEXChange?: boolean;
  billingAddress?: string;
  identityDocument?: string;
  billPersonalBusiness?: BillPersonalBusiness;
}

/**
 * Payload for POST /{lang}/deposit-grab-mercadopago
 * MercadoPago dual-tokenization: MP token (primary) + Stripe v2 token (fallback).
 */
export interface DepositGrabMercadoPagoPayload extends ReservationAttributionFields {
  restaurant: number;
  date: string;
  time: string;
  partySize: number;
  menu: number;
  area: number;
  type: 'deposit' | 'no_show';
  source: string;
  pairings: unknown[];
  veggie: boolean;
  howManyVeggie: number;
  kids: boolean;
  howManyKids: number;
  allergies: string;
  pets: boolean;
  accesibility: boolean;
  gratuity: number;
  giftCardToken: string | null;
  suggestedFromWaitingListAlternatives: boolean;
  rescheduleId?: number;
  cardToken: string;
  cardTokenStripe: string;
  paymentMethod: string;
  temp1: string;
  temp3: string;
  temp4: string;
  temp6: string;
  salesTax: number;
  serviceIncluded: boolean;
  tipChargePercent: string;
  payWithCard: boolean;
  agentReservation: boolean;
  partySizeType: string;
  AMEXChange?: boolean;
  identityDocument: number;
  billingAddress?: string;
  billPersonalBusiness?: BillPersonalBusiness;
}

/**
 * Response from POST /{lang}/traditional-grab and deposit-grab-stripe
 */
export interface CreateReservationResponse {
  reservationId: string;
  uniqueId?: string;
  // 3DS fields (present when card requires Strong Customer Authentication)
  requires_action?: boolean;
  client_secret?: string;
  payment_intent_id?: string;
  reservation_id?: number;
}

/**
 * Create a traditional reservation (no guarantee/deposit).
 * POST /api/{lang}/traditional-grab
 *
 * The legacy API expects the payload wrapped in { data: ... }
 * For traditional, fee is INSIDE the data payload.
 */
export async function createTraditionalReservation(
  lang: string,
  payload: TraditionalGrabPayload,
): Promise<CreateReservationResponse> {
  return api.post<CreateReservationResponse>(
    `/api/${lang}/traditional-grab`,
    { data: payload },
  );
}

/**
 * Create a deposit/no-show reservation via Stripe.
 * POST /api/{lang}/deposit-grab-stripe
 *
 * Backend recalculates fee from salesTax/tipChargePercent inside data.
 */
export async function createDepositReservation(
  lang: string,
  payload: DepositGrabPayload,
): Promise<CreateReservationResponse> {
  return api.post<CreateReservationResponse>(
    `/api/${lang}/deposit-grab-stripe`,
    { data: payload },
  );
}

/**
 * Create a deposit/no-show reservation via MercadoPago.
 * POST /api/{lang}/deposit-grab-mercadopago
 *
 * Sends dual tokens (MP + Stripe v2 fallback) and encrypted card data.
 */
export async function createMercadoPagoDepositReservation(
  lang: string,
  payload: DepositGrabMercadoPagoPayload,
): Promise<CreateReservationResponse> {
  return api.post<CreateReservationResponse>(
    `/api/${lang}/deposit-grab-mercadopago`,
    { data: payload },
  );
}

/**
 * Notify backend of a payment failure.
 * POST /api/notify-payment-failure
 */
export async function notifyPaymentFailure(
  payload: {
    restaurant?: number;
    error?: string;
    paymentMethod?: string;
    errorMsg?: string;
    paymentProcessor?: 'stripe' | 'mercadopago';
    restaurantId?: number;
    billingInfoEntered?: number;
    firstTry?: number;
  },
): Promise<void> {
  await api.post('/api/notify-payment-failure', payload);
}

/**
 * Payload for POST /{lang}/request-booking (early access)
 * Same structure as TraditionalGrabPayload — no payment fields.
 */
export interface EarlyAccessGrabPayload extends ReservationAttributionFields {
  restaurant: number;
  date: string;
  time: string;
  partySize: number;
  menu: number;
  area: number;
  type: 'traditional';
  source: string;
  pairings: unknown[];
  veggie: boolean;
  howManyVeggie: number;
  kids: boolean;
  howManyKids: number;
  allergies: string;
  pets: boolean;
  accesibility: boolean;
  fee: number;
  gratuity: number;
  giftCardToken: string | null;
  suggestedFromWaitingListAlternatives: boolean;
}

/**
 * Create an early access (request) reservation.
 * POST /api/{lang}/request-booking
 *
 * No guarantee/payment required — the restaurant approves manually.
 */
export async function createEarlyAccessReservation(
  lang: string,
  payload: EarlyAccessGrabPayload,
): Promise<CreateReservationResponse> {
  return api.post<CreateReservationResponse>(
    `/api/${lang}/request-booking`,
    { data: payload },
  );
}

/**
 * Response from GET /get-reservation-data?token={token}
 * Used by standalone confirm-early-access page and confirm-guarantee page.
 */
export interface ReservationDataResponse {
  id: number;
  uniqueId: string;
  date: string;
  time: string;
  partySize: number;
  status: string;
  cancelled: boolean;
  fpStatus?: string;
  confirmStatus?: boolean;
  walkIn?: boolean;
  restaurant: {
    id: number;
    name: string;
  };
  // Guarantee-specific fields (populated for confirm-guarantee flow)
  guaranteeStatus?: string;
  feeDeposit?: number;
  feeDepositUSD?: number;
  type?: string;
  resDate?: { date: string };
  resTime?: { date: string };
  cancellationPolicy?: string;
  guestName?: string;
  guestEmail?: string;
  guestPhone?: string;
  inWaitingList?: boolean;
  serviceType?: string;
}

/**
 * Fetch reservation data by token.
 * GET /api/get-reservation-data?token={token}
 */
export async function getReservationData(
  token: string,
): Promise<ReservationDataResponse> {
  return api.get<ReservationDataResponse>('/api/get-reservation-data', {
    params: { token },
  });
}

/**
 * Confirm an early access reservation.
 * POST /api/confirm-booking/{token}
 */
export async function confirmEarlyAccessBooking(
  token: string,
): Promise<void> {
  await api.post(`/api/confirm-booking/${token}`);
}

/**
 * Fetch reservation data by encrypted token (reservation status page).
 * GET /api/get-reservation-data?safeEncrypted=true&token={token}
 */
export async function getReservationDataEncrypted(
  token: string,
): Promise<ReservationDataResponse> {
  return api.get<ReservationDataResponse>('/api/get-reservation-data', {
    params: { safeEncrypted: 'true', token },
  });
}

/**
 * Confirm a reservation from an email link.
 * POST /api/confirm-reservation
 * The backend decrypts the ID, confirms if status is booked/seated,
 * and returns reservation details.
 */
export interface ConfirmReservationResponse {
  status: 'success' | 'invalid_res' | 'res_not_found';
  name?: string;
  resDate?: { date: string };
  resTime?: { date: string };
  partySize?: number;
  serviceType?: string;
  allergies?: string;
  guestNotes?: string;
  contact?: string;
}

export async function confirmReservation(
  encryptedId: string,
): Promise<ConfirmReservationResponse> {
  return api.post<ConfirmReservationResponse>('/api/confirm-reservation', {
    reservation: encryptedId,
  });
}

/**
 * Actions accepted by POST /api/reservation-action.
 * - `confirm` / `cancel`: state transitions (no extra params).
 * - `reschedule`: same-day time change — requires `newResTime` ("YYYY-MM-DD HH:MM").
 *   A different date returns 422 `date_change_unsupported`, so callers must only
 *   offer times on the reservation's existing date.
 * - `change_party_size`: requires `party_size` (int >= 1).
 */
export type ReservationAction = 'confirm' | 'cancel' | 'reschedule' | 'change_party_size';

/**
 * Body returned by POST /api/reservation-action.
 * On success: `{ status: 'success' }`. On failure the backend returns a 4xx
 * whose body carries a machine-readable `status` (e.g. `no_availability`,
 * `past_cutoff`, `guaranteed_modify_disabled`) and a human `message`.
 * `cancellationPolicyHours` accompanies `past_cutoff`.
 */
export interface ReservationActionResult {
  status?: string;
  message?: string;
  cancellationPolicyHours?: number;
}

/**
 * Optional params for the modify actions.
 */
export interface ReservationActionExtra {
  /** Same-day reschedule target, "YYYY-MM-DD HH:MM". */
  newResTime?: string;
  /** New party size (int >= 1). */
  party_size?: number;
}

/**
 * Perform an action on a reservation (confirm, cancel, reschedule, change party size).
 * POST /api/reservation-action
 *
 * Returns the response body so callers can read the backend `status`/`message`.
 * On a non-2xx response the api client throws {@link ApiError} (carries `.status`,
 * `.message` and `.serverCode` parsed from the body).
 */
export async function postReservationAction(
  token: string,
  action: ReservationAction,
  extra?: ReservationActionExtra,
): Promise<ReservationActionResult> {
  return api.post<ReservationActionResult>('/api/reservation-action', {
    token,
    action,
    ...extra,
  });
}

/**
 * Submit a booking intent ("contact concierge") when no availability.
 * POST /api/restaurant/{subdomain}/intent
 */
export async function submitBookingIntent(
  subdomain: string,
  payload: { partySize: number; date: string; serviceType: string },
): Promise<void> {
  await api.post(`/api/restaurant/${subdomain}/intent`, payload);
}

/**
 * Confirm a 3DS-authenticated payment.
 * POST /api/confirm-3ds-payment
 *
 * Called after the customer completes the 3DS challenge via Stripe's handleNextAction.
 */
export interface Confirm3dsResponse {
  reservationId: number;
  fee?: number;
  currency?: string;
  paymentProcessor?: string;
  status?: string;
}

export async function confirm3dsPayment(
  reservationId: number,
  paymentIntentId: string,
): Promise<Confirm3dsResponse> {
  return api.post<Confirm3dsResponse>('/api/confirm-3ds-payment', {
    reservation_id: reservationId,
    payment_intent_id: paymentIntentId,
  });
}

/**
 * Cancel a pending 3DS reservation after the customer fails/abandons the challenge.
 * POST /api/cancel-3ds-payment
 */
export async function cancel3dsPayment(
  reservationId: number,
): Promise<void> {
  await api.post('/api/cancel-3ds-payment', {
    reservation_id: reservationId,
  });
}

/**
 * Payload for POST /{lang}/reservation-note
 * Matches the legacy GrabStep3 endpoint.
 */
export interface ReservationNotePayload {
  resId: string;
  note: string;
  occasion?: string;
  languageService?: string;
  promoCode?: string;
}

/**
 * Save reservation note, occasion, promo code, and service language.
 * POST /api/{lang}/reservation-note
 */
export async function saveReservationNote(
  lang: string,
  payload: ReservationNotePayload,
): Promise<void> {
  await api.post(`/api/${lang}/reservation-note`, payload);
}

/**
 * Companion data for a reservation
 */
export interface CompanionData {
  name: string;
  email?: string;
  phone?: string;
  id?: number;
  isRegular?: boolean;
  notes?: string;
  treatment?: string;
  relationship?: string;
}

/**
 * Save companions for a reservation.
 * POST /api/companions
 */
export async function saveCompanions(
  payload: { reservationId: string; companions: CompanionData[] },
): Promise<void> {
  await api.post('/api/companions', payload);
}

/**
 * Payload for POST /save-guarantee
 * Standalone guarantee payment after reservation was created.
 */
export interface SaveGuaranteePayload {
  reservation_id: number;
  cardToken: string;
  AMEXChange: boolean;
  paymentMethod: string;
  // Encrypted reservation id from the email link. Required by the backend
  // to authorize the charge when the guest is not authenticated.
  token?: string;
  cardTokenStripe?: string;
  temp1?: string;
  temp3?: string;
  temp4?: string;
  temp6?: string;
}

/**
 * Save guarantee payment for an existing reservation.
 * POST /api/save-guarantee
 */
export async function saveGuarantee(
  payload: SaveGuaranteePayload,
): Promise<CreateReservationResponse> {
  return api.post<CreateReservationResponse>('/api/save-guarantee', payload);
}
